import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useTurnoutData, usePublicCampaigns } from '../hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Vote, 
  Users, 
  CheckCircle2, 
  Calendar, 
  RefreshCw,
  TrendingUp,
  MapPin,
  Clock,
  BarChart3,
  ArrowLeft
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Voting Day Countdown for Public Page
const PublicCountdown = ({ votingDay, votingStartTime, votingEndTime }) => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isToday: false });

  useEffect(() => {
    if (!votingDay) return;

    const calculateCountdown = () => {
      const now = new Date();
      const target = new Date(votingDay);
      const diff = target - now;
      
      // Check if today
      const today = new Date();
      const targetDate = new Date(votingDay);
      const isToday = today.toDateString() === targetDate.toDateString();

      if (diff <= 0 && !isToday) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true, isToday: false });
        return;
      }

      if (isToday) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isToday: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds, isPast: false, isToday: false });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [votingDay]);

  // Format time for display (HH:MM -> h:mm AM/PM)
  const formatVotingTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (!votingDay) return null;

  if (countdown.isToday) {
    return (
      <div className="text-center py-4 space-y-3">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full text-xl font-bold animate-pulse">
          <Vote className="h-6 w-6" />
          VOTING DAY - LIVE
        </div>
        {(votingStartTime || votingEndTime) && (
          <div className="flex items-center justify-center gap-4 text-white/90">
            <Clock className="h-5 w-5" />
            <span className="text-lg font-medium">
              {votingStartTime && formatVotingTime(votingStartTime)}
              {votingStartTime && votingEndTime && ' - '}
              {votingEndTime && formatVotingTime(votingEndTime)}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (countdown.isPast) {
    return (
      <div className="text-center py-4">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-full text-lg font-semibold">
          <CheckCircle2 className="h-5 w-5" />
          Voting Completed
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-4">
      <div className="flex justify-center gap-4">
        {[
          { value: countdown.days, label: 'Days' },
          { value: countdown.hours, label: 'Hours' },
          { value: countdown.minutes, label: 'Minutes' },
          { value: countdown.seconds, label: 'Seconds' }
        ].map((item, idx) => (
          <div key={idx} className="text-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[80px]">
              <div className="text-4xl font-bold text-white">{item.value}</div>
              <div className="text-sm text-white/80">{item.label}</div>
            </div>
          </div>
        ))}
      </div>
      {(votingStartTime || votingEndTime) && (
        <div className="flex items-center justify-center gap-3 text-white/80">
          <Clock className="h-4 w-4" />
          <span>
            Voting Hours: {votingStartTime && formatVotingTime(votingStartTime)}
            {votingStartTime && votingEndTime && ' - '}
            {votingEndTime && formatVotingTime(votingEndTime)}
          </span>
        </div>
      )}
    </div>
  );
};

export default function PublicTurnoutPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [selectedCampaign, setSelectedCampaign] = useState(campaignId || '');
  const [accessDenied, setAccessDenied] = useState(false);

  useRegisterSocketRooms(selectedCampaign ? [`turnout:${selectedCampaign}`] : []);

  const { data: campaigns = [] } = usePublicCampaigns();
  const { data, isLoading: loading, error, dataUpdatedAt, refetch } = useTurnoutData(selectedCampaign, token);

  useEffect(() => {
    if (!selectedCampaign && campaigns.length > 0) {
      setSelectedCampaign(campaigns[0].campaign_id);
    }
  }, [campaigns, selectedCampaign]);

  useEffect(() => {
    if (error?.response?.status === 403) setAccessDenied(true);
    else setAccessDenied(false);
  }, [error]);

  const handleCampaignChange = (value) => {
    setSelectedCampaign(value);
    const newUrl = token ? `/turnout/${value}?token=${token}` : `/turnout/${value}`;
    navigate(newUrl);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const turnoutData = data ?? null;
  const remaining = turnoutData ? turnoutData.total_registered - turnoutData.total_voted : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Vote className="h-10 w-10 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {data?.app_name || process.env.REACT_APP_APP_NAME || 'RajjePulse'}
                </h1>
                <p className="text-white/70 text-sm">Live Voter Turnout Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedCampaign} onValueChange={handleCampaignChange}>
                <SelectTrigger className="w-[280px] bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select Campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map(c => (
                    <SelectItem key={c.campaign_id} value={c.campaign_id}>
                      {c.name} - {c.constituency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => refetch()}
                className="text-white hover:bg-white/10"
              >
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {loading && !data && !accessDenied ? (
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/20 border-t-white"></div>
        </div>
      ) : accessDenied ? (
        <div className="flex flex-col items-center justify-center h-[60vh] text-white">
          <div className="p-6 bg-red-500/20 rounded-full mb-6">
            <Vote className="h-16 w-16 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-white/70 text-center max-w-md">
            This dashboard requires an access token. Please use the link provided by the campaign administrator.
          </p>
        </div>
      ) : !data ? (
        <div className="flex flex-col items-center justify-center h-[60vh] text-white">
          <Vote className="h-16 w-16 opacity-50 mb-4" />
          <h2 className="text-2xl font-bold mb-2">No Campaign Selected</h2>
          <p className="text-white/70">Please select a campaign to view turnout data</p>
        </div>
      ) : (
        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Campaign Info */}
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.campaign_name}
            </h2>
            <div className="flex items-center justify-center gap-4 text-white/70">
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {data.constituency_name}
              </span>
              {data.campaign_type && (
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {data.campaign_type}
                </span>
              )}
            </div>
            {data.voting_day && (
              <div className="mt-4">
                <p className="text-white/60 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  {formatDate(data.voting_day)}
                </p>
                <PublicCountdown 
                  votingDay={data.voting_day} 
                  votingStartTime={data.voting_start_time}
                  votingEndTime={data.voting_end_time}
                />
              </div>
            )}
          </div>

          {/* Main Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="h-12 w-12 mx-auto mb-3 text-blue-400" />
                  <p className="text-white/70 text-sm uppercase tracking-wide">Total Registered</p>
                  <p className="text-5xl font-bold mt-2">{data.total_registered.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/30 to-green-600/30 backdrop-blur-md border-green-400/30 text-white">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-400" />
                  <p className="text-white/70 text-sm uppercase tracking-wide">Total Voted</p>
                  <p className="text-5xl font-bold mt-2">{data.total_voted.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-purple-400" />
                  <p className="text-white/70 text-sm uppercase tracking-wide">Turnout</p>
                  <p className="text-5xl font-bold mt-2">{data.turnout_percentage}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Large Progress Bar */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20 mb-8">
            <CardContent className="py-8">
              <div className="relative">
                <div className="h-12 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-4"
                    style={{ width: `${data.turnout_percentage}%` }}
                  >
                    {data.turnout_percentage > 10 && (
                      <span className="text-white font-bold text-lg">{data.turnout_percentage}%</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between mt-3 text-white/70 text-sm">
                  <span>{data.total_voted.toLocaleString()} voted</span>
                  <span>{remaining.toLocaleString()} remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Box Stats */}
          {data.box_stats?.length > 0 && (
            <Card className="bg-white/10 backdrop-blur-md border-white/20 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Turnout by Voting Box
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.box_stats} layout="vertical" margin={{ left: 20, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis type="number" stroke="rgba(255,255,255,0.5)" />
                      <YAxis 
                        type="category" 
                        dataKey="box_name" 
                        stroke="rgba(255,255,255,0.5)"
                        width={120}
                        tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0,0,0,0.8)', 
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: 'white' }}
                        formatter={(value, name) => [value.toLocaleString(), name === 'voted' ? 'Voted' : 'Registered']}
                      />
                      <Bar dataKey="registered" fill="rgba(255,255,255,0.2)" name="Registered" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="voted" fill="#22c55e" name="Voted" radius={[0, 4, 4, 0]}>
                        {data.box_stats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${142 + index * 5}, 70%, 50%)`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Box cards for mobile */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                  {data.box_stats.map((box, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white/5 rounded-lg p-3 text-center"
                    >
                      <p className="text-white font-medium text-sm truncate">{box.box_name}</p>
                      <p className="text-green-400 text-2xl font-bold">{box.voted}</p>
                      <p className="text-white/50 text-xs">of {box.registered} ({box.turnout_percentage}%)</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Voting Timeline */}
          {data.voting_timeline?.length > 0 && (
            <Card className="bg-white/10 backdrop-blur-md border-white/20 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Voting Activity Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.voting_timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="hour" 
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 10 }}
                        tickFormatter={(value) => value.split(' ')[1] || value}
                      />
                      <YAxis stroke="rgba(255,255,255,0.5)" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0,0,0,0.8)', 
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: 'white' }}
                        formatter={(value) => [value.toLocaleString(), 'Votes']}
                      />
                      <Bar dataKey="votes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="text-center text-white/50 text-sm py-4">
            <p>
              Last updated: {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '-'}
              <span className="mx-2">•</span>
              Auto-refreshes every 15 seconds
            </p>
          </div>
        </main>
      )}
    </div>
  );
}
